import styled from '@emotion/styled';
import { FormikProps, FormikValues } from 'formik/dist/types';
import {
    Input,
    InputType,
    SelectField,
    MultiSelectField,
    Option,
    pxToRem,
} from '@modernhealth/vital-components';
import React from 'react';
import { TFunction } from 'i18next';

export const renderSelectWithFormik =
    <T extends FormikValues = FormikValues, N extends string = Extract<keyof T, string>>(
        {
            handleChange,
            errors,
            touched,
            initialValues,
            setFieldValue,
            setFieldTouched,
        }: FormikProps<T>,
        t: TFunction,
    ) =>
    (name: N, options: Option[], isMulti = false) => {
        if (isMulti) {
            return (
                <Field>
                    <MultiSelectField<Option>
                        fieldId={name}
                        options={options}
                        onChange={options => {
                            setFieldValue(name, options);
                        }}
                        label={t(`dependents:form.label.${name}`)}
                        error={
                            touched?.[name] &&
                            errors &&
                            errors[name] &&
                            t(`dependents:${errors[name]}`)
                        }
                        onBlur={() => setFieldTouched(name)}
                        defaultValue={initialValues[name]}
                    />
                </Field>
            );
        }

        return (
            <Field>
                <SelectField
                    fieldId={name}
                    options={options}
                    onChange={(option: Option) => handleChange(name)(option?.value ?? '')}
                    label={t(`dependents:form.label.${name}`)}
                    error={
                        touched?.[name] && errors && errors[name] && t(`dependents:${errors[name]}`)
                    }
                    defaultValue={initialValues[name]}
                    onBlur={() => setFieldTouched(name)}
                />
            </Field>
        );
    };

export const renderInputWithFormik =
    <T extends FormikValues = FormikValues, N extends string = Extract<keyof T, string>>(
        { handleChange, errors, touched, initialValues, values, setFieldTouched }: FormikProps<T>,
        t: TFunction,
    ) =>
    (name: N, type: InputType = InputType.TextField) =>
        (
            <Field>
                <Input
                    fieldId={name}
                    type={type}
                    label={t(`dependents:form.label.${name}`)}
                    onChange={handleChange(name as string)}
                    error={
                        touched?.[name] && errors && errors[name] && t(`dependents:${errors[name]}`)
                    }
                    initialValue={initialValues[name]}
                    value={type !== InputType.DatePicker ? values[name] : undefined}
                    useDefaultPlaceholder={type === InputType.TextArea}
                    testID={`InviteDependent.${name}`}
                    onBlur={() => setFieldTouched(name)}
                />
            </Field>
        );

const Field = styled.div({
    marginBottom: pxToRem(32),
});
